import { useState } from "react";
import { Phone, Mail, MapPin, Send, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE, SERVICES } from "../lib/site";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Request received — we'll be in touch within 24 hours.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="section pt-16 md:pt-24" data-testid="contact-hero">
        <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="overline">Contact</p>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl text-white font-light leading-[1.05]">
              Let's start your <span className="font-script text-[var(--gold)]">project</span>.
            </h1>
            <p className="mt-6 text-white/60 text-lg leading-relaxed">
              Send us a note, give us a call, or stop by. We respond to all estimate requests within
              24 hours.
            </p>

            <div className="mt-12 space-y-6">
              <a href={SITE.phoneHref} className="flex items-start gap-4 group" data-testid="contact-phone-link">
                <div className="w-12 h-12 border border-[var(--gold)]/40 flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <Phone size={18} className="text-[var(--gold)]" />
                </div>
                <div>
                  <p className="overline">Customer Service</p>
                  <p className="text-white text-lg group-hover:text-[var(--gold)] transition-colors">{SITE.phone}</p>
                </div>
              </a>
              <a href={SITE.emailHref} className="flex items-start gap-4 group" data-testid="contact-email-link">
                <div className="w-12 h-12 border border-[var(--gold)]/40 flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <Mail size={18} className="text-[var(--gold)]" />
                </div>
                <div>
                  <p className="overline">Email</p>
                  <p className="text-white text-lg break-all group-hover:text-[var(--gold)] transition-colors">{SITE.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-[var(--gold)]/40 flex items-center justify-center">
                  <MapPin size={18} className="text-[var(--gold)]" />
                </div>
                <div>
                  <p className="overline">Address</p>
                  <p className="text-white text-lg">{SITE.address}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href={SITE.phoneHref} className="btn-primary !py-3 !px-5" data-testid="contact-call-btn">
                <Phone size={16} /> Call Now
              </a>
              <a href={SITE.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-3 !px-5" data-testid="contact-review-btn">
                <Star size={16} /> Leave a Review
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/5 p-8 md:p-12" data-testid="contact-form">
              <p className="overline">Free Estimate Request</p>
              <h2 className="mt-3 font-display text-3xl text-white font-light">Tell us about your project</h2>

              {submitted && (
                <div className="mt-6 flex items-start gap-3 border border-[var(--gold)]/30 bg-[var(--gold)]/5 p-4 text-[var(--gold)]" data-testid="contact-success-msg">
                  <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    Thanks — your request has been received. We'll reach out within 24 hours.
                  </p>
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Name *" data-testid="field-name">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white"
                    placeholder="Full name"
                    required
                    data-testid="input-name"
                  />
                </Field>
                <Field label="Phone *" data-testid="field-phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white"
                    placeholder="(xxx) xxx-xxxx"
                    required
                    data-testid="input-phone"
                  />
                </Field>
                <Field label="Email *" data-testid="field-email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white"
                    placeholder="you@example.com"
                    required
                    data-testid="input-email"
                  />
                </Field>
                <Field label="Service Needed" data-testid="field-service">
                  <select
                    value={form.service}
                    onChange={update("service")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white appearance-none"
                    data-testid="input-service"
                  >
                    <option value="" className="bg-black">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title} className="bg-black">{s.title}</option>
                    ))}
                    <option value="Other" className="bg-black">Other / Not sure</option>
                  </select>
                </Field>
              </div>

              <Field label="Message *" className="mt-6" data-testid="field-message">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white resize-none"
                  placeholder="Tell us about your project — square footage, timeline, anything we should know…"
                  required
                  data-testid="input-message"
                />
              </Field>

              <button type="submit" disabled={submitting} className="btn-primary mt-10" data-testid="contact-submit-btn">
                {submitting ? "Sending…" : "Request Estimate"} <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children, className = "", ...rest }) {
  return (
    <label className={`block ${className}`} {...rest}>
      <span className="overline">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
